import { getViewerUtcOffsetMinutes } from "../../lib/date-ymd.ts";
import { http } from "./http.ts";
import type {
  ApiSuccess,
  CameraTimeline,
  PaginatedResult,
  Recording,
  RecordingListQuery,
} from "./types.ts";

export const recordingsApi = {
  async listForCamera(
    cameraId: string,
    query: Omit<RecordingListQuery, "cameraId"> = {},
    signal?: AbortSignal,
  ): Promise<PaginatedResult<Recording>> {
    const res = await http.get<ApiSuccess<PaginatedResult<Recording>>>(
      `/api/cameras/${cameraId}/recordings`,
      { params: stripUndefined(query), signal },
    );
    return res.data.data;
  },

  async getTimeline(
    cameraId: string,
    query: Pick<
      RecordingListQuery,
      "date" | "start" | "end" | "utcOffsetMinutes"
    > = {},
    signal?: AbortSignal,
  ): Promise<CameraTimeline> {
    const params = stripUndefined({
      ...query,
      utcOffsetMinutes: query.utcOffsetMinutes ?? getViewerUtcOffsetMinutes(),
    });
    const res = await http.get<ApiSuccess<CameraTimeline>>(
      `/api/cameras/${cameraId}/timeline`,
      {
        params,
        signal,
      },
    );
    return res.data.data;
  },

  /**
   * POST /api/recordings/export — returns an MP4 Blob.
   */
  async exportClip(
    payload: {
      cameraId: string;
      start?: string;
      end?: string;
      utcOffsetMinutes?: number;
      stream?: string;
      segmentName?: string;
      segmentStart?: string;
    },
    signal?: AbortSignal,
  ): Promise<Blob> {
    const res = await http.post("/api/recordings/export", payload, {
      responseType: "blob",
      signal,
      timeout: 610_000,
    });
    return res.data;
  },

  /**
   * GET /api/cameras/:id/snapshot — returns a JPEG Blob.
   */
  async liveSnapshotJpeg(
    cameraId: string,
    options?: { force?: boolean },
    signal?: AbortSignal,
  ): Promise<Blob> {
    const params =
      options?.force === true ? { refresh: 1, force: 1 } : undefined;
    const res = await http.get(`/api/cameras/${cameraId}/snapshot`, {
      responseType: "blob",
      params,
      signal,
      timeout: 45_000,
    });
    return res.data;
  },

  getTimelinePlaylistUrl(
    cameraId: string,
    query: Pick<
      RecordingListQuery,
      "date" | "start" | "end" | "utcOffsetMinutes" | "stream"
    > = {},
  ): string {
    const params = new URLSearchParams();
    const merged = stripUndefined({
      ...query,
      utcOffsetMinutes: query.utcOffsetMinutes ?? getViewerUtcOffsetMinutes(),
    });
    for (const [key, value] of Object.entries(merged)) {
      params.set(key, String(value));
    }
    const queryString = params.toString();
    return `/api/cameras/${cameraId}/timeline.m3u8${queryString ? `?${queryString}` : ""}`;
  },
};

function stripUndefined<T extends object>(obj: T): Partial<T> {
  const out: Partial<T> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined && v !== "" && v !== null) {
      (out as Record<string, unknown>)[k] = v;
    }
  }
  return out;
}
